/*
 * decaffeinate suggestions:
 * DS102: Remove unnecessary code created because of implicit returns
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */

import Bluebird from 'bluebird';

import { Cookies } from '@trello/cookies';
import { getDateBefore } from '@trello/dates';
import { ApiError } from '@trello/error-handling';
import { getExperimentValueAsync } from '@trello/feature-gate-client';
// eslint-disable-next-line no-restricted-imports
import $ from '@trello/jquery';
import type { PIIString } from '@trello/privacy';
import { getLocation as getLocationFromRouter } from '@trello/router';
import { navigate } from '@trello/router/navigate';
import { TrelloStorage } from '@trello/storage';
import { escapeReturnUrl } from '@trello/urls';
import { importWithRetry } from '@trello/use-lazy-component';

import { errorPage } from 'app/scripts/controller/errorPage';
import { Auth } from 'app/scripts/db/Auth';
import { ModelLoader } from 'app/scripts/db/model-loader';
import { l } from 'app/scripts/lib/localize';
import { Util } from 'app/scripts/lib/util';
import type { Member } from 'app/scripts/models/Member';
import {
  isValidMoonshotStep,
  moonshotCampaign,
  MoonshotSteps,
} from 'app/src/components/Moonshot';
import { validateReturnUrl } from 'app/src/components/Moonshot/validateReturnUrl';
import {
  isValidSplitScreenOnboardingStep,
  splitScreenOnboardingCampaign,
  type SplitScreenOnboardingStep,
} from 'app/src/components/SplitScreenOnboardingCampaign';
import { getSpinner } from 'app/src/getSpinner';
import { controllerEvents } from './controllerEvents';
import { focusContent } from './focusContent';
import { setTitleAndLocation } from './setTitleAndLocation';

type ViewClass = 'MemberActivityView' | 'MemberHomeView' | 'MemberProfileView';

// Variables used to determine if we should pretend the user is new, for testing
// purposes
let simulateNewUser: string | null = null;

function getViewClass(viewClass: ViewClass) {
  switch (viewClass) {
    case 'MemberHomeView':
      return importWithRetry(() =>
        import(
          /* webpackChunkName: "member-home-view" */ 'app/scripts/views/member/MemberHomeView'
        ).then((module) => module.MemberHomeView),
      );
    case 'MemberActivityView':
      return importWithRetry(() =>
        import(
          /* webpackChunkName: "member-activity-view" */ 'app/scripts/views/member/MemberActivityView'
        ).then((module) => module.MemberActivityView),
      );
    case 'MemberProfileView':
      return importWithRetry(() =>
        import(
          /* webpackChunkName: "member-profile-view" */ 'app/scripts/views/member/MemberProfileView'
        ).then(({ MemberProfileView }) => MemberProfileView),
      );
    default:
      return Bluebird.resolve(viewClass);
  }
}

const shouldRedirectToOnboardingRoute = async (member: Member) => {
  // If either campaign has already been dismissed, we must never redirect to onboarding
  if (
    member.campaignIsDismissed(moonshotCampaign.name) ||
    member.campaignIsDismissed(splitScreenOnboardingCampaign.name)
  ) {
    return false;
  }

  if (
    !member.campaignIsActive(moonshotCampaign.name) &&
    !member.campaignIsActive(splitScreenOnboardingCampaign.name)
  ) {
    return false;
  }

  const memberSplitScreenOnboardingCampaign = member.getCampaign(
    splitScreenOnboardingCampaign.name,
  );
  const memberMoonshotCampaign = member.getCampaign(moonshotCampaign.name);

  const prioritizedCampaign =
    (await getExperimentValueAsync(
      'new_user_onboarding_flow_and_in-product_discovery',
      'campaign',
      false,
    )) === 'newUserSplitScreenOnboarding'
      ? memberSplitScreenOnboardingCampaign
      : memberMoonshotCampaign;

  const activeCampaign =
    memberSplitScreenOnboardingCampaign ?? memberMoonshotCampaign;

  // If the user happens to be in both campaigns, we want to prioritize whichever variant the user is served.
  // However, if the prioritized campaign is undefined, we'll just go with the active campaign.
  const campaign = prioritizedCampaign ?? activeCampaign;

  if (!campaign) {
    return false;
  }

  // Most of the Moonshot flow happens in the /welcome-to-trello route, with the
  // exception of the WELCOME step (this is a popup in a workspace page).
  //
  // Redirect to /welcome-to-trello if the user is in any step other than WELCOME.
  // If the user is on WELCOME, ensure they are not sent back to
  // /welcome-to-trello
  if (campaign.name === moonshotCampaign.name) {
    if (
      isValidMoonshotStep(campaign.currentStep) &&
      campaign.currentStep !== MoonshotSteps.WELCOME
    ) {
      return true;
    } else {
      member.dismissCampaign(moonshotCampaign.name);
      return false;
    }
  } else if (
    campaign.name === splitScreenOnboardingCampaign.name &&
    isValidSplitScreenOnboardingStep(
      campaign.currentStep as SplitScreenOnboardingStep,
    )
  ) {
    return true;
  }

  return false;
};

const onboardingRedirectPath = (member: Member) => {
  if (!simulateNewUser) {
    simulateNewUser = Cookies.get('simulateNewUser') || null;
  }

  const isNewUser = Util.idToDate(Auth.myId()) > getDateBefore({ minutes: 3 });

  if (!isNewUser && !simulateNewUser) {
    return;
  }

  if (member.boardList.length !== 0 || member.organizationList.length !== 0) {
    return;
  }

  const hasGoneThroughOnboardingExperience =
    member.campaignIsDismissed(moonshotCampaign.name) ||
    Auth.me().isDismissed('create-first-board');

  if (!hasGoneThroughOnboardingExperience) {
    const { pathname } = getLocationFromRouter();
    const returnUrl = validateReturnUrl(pathname)
      ? escapeReturnUrl(pathname)
      : undefined;
    const queryParams = returnUrl ? `?returnUrl=${returnUrl}` : '';
    return `/welcome-to-trello${queryParams}`;
  }
};

export const memberPage = function ({
  username,
  loadFn,
  preloadFn, //get member info for header
  // viewClass,
  viewClassName,
  getLocation,
  bodyClass,
  product,
  orgname,
  titleKey,
  showHomeBoardsTab,
  showGettingStarted,
  templatesData,
}: {
  username: PIIString;
  loadFn?: string;
  preloadFn?: string;
  viewClassName: ViewClass;
  getLocation: (...args: unknown[]) => string;
  bodyClass?: string;
  product?: number;
  orgname?: string | null;
  titleKey: string;
  showHomeBoardsTab?: boolean;
  showGettingStarted?: boolean;
  templatesData?: unknown;
}) {
  return Bluebird.using(getViewClass(viewClassName), (viewClass) => {
    // eslint-disable-next-line eqeqeq
    if (username == null) {
      username = Auth.myUsername();
    }

    return Bluebird.using(getSpinner(), async () => {
      try {
        const member =
          preloadFn || loadFn
            ? // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
              await ModelLoader[preloadFn || (loadFn as string)](username)
            : Auth.me();

        const loadPromise =
          preloadFn && loadFn
            ? // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
              ModelLoader[loadFn](username)
            : Bluebird.resolve();

        if (await shouldRedirectToOnboardingRoute(member)) {
          const { pathname } = getLocationFromRouter();
          const returnUrl = validateReturnUrl(pathname)
            ? escapeReturnUrl(pathname)
            : undefined;
          const queryParams = returnUrl ? `?returnUrl=${returnUrl}` : '';
          /**
           * This actually renders the MoonshotOrRedirect component,
           * which will then decide where to further redirect the user.
           */
          return navigate(`/welcome-to-trello${queryParams}`, {
            trigger: true,
            replace: true,
          });
        }
        const onboardingBoardUrl = TrelloStorage.get(
          `onboardingBoardUrl-${member.id}`,
        );

        if (onboardingBoardUrl) {
          TrelloStorage.unset(`onboardingBoardUrl-${member.id}`);
          return navigate(onboardingBoardUrl, {
            trigger: true,
            replace: true,
          });
        }

        if (viewClassName === 'MemberHomeView') {
          const redirectPath = onboardingRedirectPath(member);
          if (redirectPath) {
            return navigate(redirectPath, {
              trigger: true,
              replace: true,
            });
          }
        }

        controllerEvents.trigger('clearPreviousView');
        controllerEvents.trigger('setViewType', member);

        const title =
          // eslint-disable-next-line eqeqeq
          Auth.isMe(member) && titleKey != null
            ? l(['page title', titleKey])
            : member.getMemberViewTitle();

        // Do this before creating the view, in case the view has more
        // specific logic for setting the URL
        setTitleAndLocation({
          title,
          // @ts-expect-error
          location: getLocation.call(this, member.get('username'), product),
          options: {
            replace: true,
          },
        });

        const options = {
          product,
          orgname,
          showHomeBoardsTab,
          showGettingStarted,
          templatesData,
          loadPromise,
        };

        // @ts-expect-error
        const memberView = this.topLevelView(viewClass, member, options);

        if (bodyClass) {
          $('#trello-root').addClass(bodyClass);
        } else {
          $('#trello-root').addClass('body-tabbed-page');
        }

        $('#content').html(memberView.render().el);
        focusContent();
        if (typeof memberView.getData === 'function') {
          memberView.getData();
        }
      } catch (err) {
        if (err instanceof ApiError) {
          errorPage({});
        }
        console.error(err);
      }
    });
  });
};
